import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { usersAPI, subscriptionsAPI, plansAPI } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import {
  ArrowLeft, Mail, Phone, Edit, Trash2, Plus,
  AlertTriangle, Check, X, User as UserIcon, Baby, Shield,
  Users as UsersIcon, ShoppingCart, Tag, Send
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';

const UserDetailPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { isOwner: currentIsAdmin } = useAuth();

  const [user, setUser] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddChildModal, setShowAddChildModal] = useState(false);
  const [showDeleteUserDialog, setShowDeleteUserDialog] = useState(false);
  const [showDeleteChildDialog, setShowDeleteChildDialog] = useState(null);

  const [editForm, setEditForm] = useState({ name: '', email: '', phone: '', role: 'USER', can_receive_subscriptions: false, secondary_parent_name: '', secondary_parent_phone: '' });
  const [newChild, setNewChild] = useState({ name: '', birth_date: '' });
  const [saving, setSaving] = useState(false);
  const [resending, setResending] = useState(false);

  // Cart: { personId: planId }
  const [cart, setCart] = useState({});
  // Data de start per persoană: { personId: 'YYYY-MM-DD' }
  const [customStartDates, setCustomStartDates] = useState({});
  // Import cursanți existenți: ședințe deja folosite + expirare manuală per persoană
  const [customSessionsUsed, setCustomSessionsUsed] = useState({});
  const [customExpiryDates, setCustomExpiryDates] = useState({});

  const fetchData = useCallback(async () => {
    try {
      const [userRes, subsRes, plansRes] = await Promise.all([
        usersAPI.getOne(userId),
        subscriptionsAPI.getAll({ user_id: userId }),
        plansAPI.getAll()
      ]);
      setUser(userRes.data);
      setSubscriptions(subsRes.data);
      setPlans(plansRes.data);
      setEditForm({
        name: userRes.data.name,
        email: userRes.data.email || '',
        phone: userRes.data.phone || '',
        role: userRes.data.role,
        can_receive_subscriptions: userRes.data.can_receive_subscriptions || false,
        secondary_parent_name: userRes.data.secondary_parent_name || '',
        secondary_parent_phone: userRes.data.secondary_parent_phone || '',
      });
    } catch {
      toast.error('Eroare la încărcarea datelor');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleResendSetup = async () => {
    setResending(true);
    try {
      await usersAPI.resendSetup(userId);
      toast.success('Email de configurare parolă retrimis');
    } catch {
      toast.error('Eroare la retrimiterea emailului');
    } finally {
      setResending(false);
    }
  };

  // Lista persoanelor din familie
  const persons = useMemo(() => {
    if (!user) return [];
    const list = [{ id: user.id, type: 'user', name: user.name, isUser: true }];
    // Show secondary parent whenever name + id exist (no longer requires is_shared_family flag)
    if (user.secondary_parent_id && user.secondary_parent_name) {
      list.push({
        id: user.secondary_parent_id,
        type: 'secondary_parent',
        name: user.secondary_parent_name,
        isSecondaryParent: true,
      });
    }
    (user.children || []).forEach(c => list.push({
      id: c.id, type: 'child', name: c.name, birth_date: c.birth_date, qr_token: c.qr_token
    }));
    return list;
  }, [user]);

  // Planuri filtrate per persoana (scope)
  const getPlansForPerson = useCallback((person) => {
    return plans.filter(p => {
      const isChildPerson = person.type === 'child';
      if (!isChildPerson && p.scope === 'child') return false;
      if (isChildPerson && p.scope === 'adult') return false;
      return true;
    });
  }, [plans]);

  // Grupare planuri dupa locatie
  const groupByLocation = (planList) => {
    return planList.reduce((acc, p) => {
      if (!acc[p.location]) acc[p.location] = [];
      acc[p.location].push(p);
      return acc;
    }, {});
  };

  // Calcul discount pentru cos
  const cartDiscount = useMemo(() => {
    if (!user) return 0;
    const childrenInCart = Object.entries(cart).filter(([pid]) => {
      const p = persons.find(x => x.id === pid);
      return p?.type === 'child';
    }).length;
    const childrenWithActiveSubs = (user.children || []).filter(c => {
      if (cart[c.id]) return false;
      return subscriptions.some(s => s.person_id === c.id && s.status === 'active');
    }).length;
    return (childrenInCart + childrenWithActiveSubs) >= 2 ? 0.1 : 0;
  }, [cart, user, subscriptions, persons]);

  const cartItems = useMemo(() => {
    return Object.entries(cart).map(([personId, planId]) => {
      const person = persons.find(p => p.id === personId);
      const plan = plans.find(p => p.id === planId);
      if (!person || !plan) return null;
      const isChild = person.type === 'child';
      const price = isChild ? plan.price * (1 - cartDiscount) : plan.price;
      return { personId, planId, person, plan, price };
    }).filter(Boolean);
  }, [cart, persons, plans, cartDiscount]);

  const cartTotal = useMemo(() => cartItems.reduce((s, i) => s + i.price, 0), [cartItems]);

  // Handlers
  const handleUpdateUser = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await usersAPI.update(userId, editForm);
      toast.success('Actualizat!');
      setShowEditModal(false);
      fetchData();
    } catch { toast.error('Eroare'); } finally { setSaving(false); }
  };

  const handleDeleteUser = async () => {
    try {
      await usersAPI.delete(userId);
      toast.success('Utilizator șters');
      navigate('/admin/utilizatori');
    } catch { toast.error('Eroare la ștergere'); }
  };

  const handleAddChild = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await usersAPI.addChild(userId, newChild);
      toast.success('Copil adăugat');
      setShowAddChildModal(false);
      setNewChild({ name: '', birth_date: '' });
      fetchData();
    } catch { toast.error('Eroare'); } finally { setSaving(false); }
  };

  const handleDeleteChild = async (childId) => {
    try {
      await usersAPI.deleteChild(userId, childId);
      toast.success('Copil șters');
      setShowDeleteChildDialog(null);
      fetchData();
    } catch { toast.error('Eroare'); }
  };

  // Construiește payload-ul unui abonament, incluzând progresul personalizat (import cursanți existenți)
  const buildSubPayload = (item) => {
    const startDate = customStartDates[item.personId];
    const usedRaw = customSessionsUsed[item.personId];
    const used = usedRaw !== undefined && usedRaw !== '' ? parseInt(usedRaw, 10) : null;
    const expiry = customExpiryDates[item.personId];
    return {
      user_id: userId,
      person_id: item.personId,
      person_type: item.person.type,
      plan_id: item.planId,
      ...(startDate ? { custom_start_date: new Date(startDate).toISOString() } : {}),
      ...(used !== null && !Number.isNaN(used) && used > 0 ? { custom_sessions_used: used } : {}),
      ...(expiry ? { custom_expires_at: new Date(expiry + 'T23:59:59').toISOString() } : {}),
    };
  };

  const handleSellCart = async () => {
    if (cartItems.length === 0) return;
    setSaving(true);
    try {
      if (cartItems.length === 1) {
        await subscriptionsAPI.create(buildSubPayload(cartItems[0]));
      } else {
        await subscriptionsAPI.createBatch({
          subscriptions: cartItems.map(buildSubPayload)
        });
      }
      toast.success(cartItems.length > 1 ? `${cartItems.length} abonamente vândute!` : 'Abonament vândut!');
      setCart({});
      setCustomStartDates({});
      setCustomSessionsUsed({});
      setCustomExpiryDates({});
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la vânzare');
    } finally { setSaving(false); }
  };

  if (loading) return <div className="p-20 text-center animate-pulse text-[#6db025] font-heading">ÎNCĂRCARE...</div>;

  const shouldShowSubUI = (person) => {
    if (person.type === 'child' || person.type === 'secondary_parent') return true;
    // Primary user only gets subscription UI if can_receive_subscriptions is enabled
    if (person.isUser) return user.can_receive_subscriptions === true;
    return false;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 md:gap-6">
        <div className="flex items-start sm:items-center gap-3 sm:gap-4 min-w-0">
          <Link to="/admin/utilizatori" className="w-10 h-10 flex-shrink-0 bg-white/5 rounded-full flex items-center justify-center text-white/40 hover:text-[#6db025] transition-all">
            <ArrowLeft size={20} />
          </Link>
          <div className="min-w-0">
            <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
              <h1 className="font-heading text-2xl sm:text-4xl font-bold text-white uppercase break-words">{user.name}</h1>
              <span className={`text-[11px] px-2 py-0.5 rounded font-black uppercase ${
                user.role === 'OWNER' ? 'bg-purple-500/20 text-purple-400' :
                user.role === 'COACH' ? 'bg-blue-500/20 text-blue-400' : 'bg-white/10 text-white/40'
              }`}>{user.role}</span>
              {user.can_receive_subscriptions && (
                <span className="text-[11px] px-2 py-0.5 rounded font-black uppercase bg-[#6db025]/10 text-[#6db025]">Participant</span>
              )}
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1">
              {user.email && <span className="text-white/40 text-sm flex items-center gap-1.5"><Mail size={12} /> {user.email}</span>}
              {user.phone && <span className="text-white/40 text-sm flex items-center gap-1.5"><Phone size={12} /> {user.phone}</span>}
            </div>
            {user.secondary_parent_name && (
              <div className="flex items-center gap-2 mt-2 px-3 py-1.5 bg-blue-500/5 border border-blue-500/10 rounded-sm">
                <UsersIcon size={12} className="text-blue-400" />
                <span className="text-blue-400/80 text-sm">
                  Partener: <span className="font-bold text-blue-400">{user.secondary_parent_name}</span>
                  {user.secondary_parent_phone && <span className="text-blue-400/50 ml-2">· {user.secondary_parent_phone}</span>}
                </span>
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {user.role === 'USER' && <button onClick={() => setShowAddChildModal(true)} className="btn-secondary flex-1 sm:flex-none justify-center px-4 py-2.5 flex items-center gap-2 text-sm font-bold uppercase"><Plus size={14} /> Copil</button>}
          {user.email && (
            <button
              onClick={handleResendSetup}
              disabled={resending}
              title="Retrimite email setare parolă"
              className="btn-secondary flex-1 sm:flex-none justify-center px-4 py-2.5 flex items-center gap-2 text-sm font-bold uppercase disabled:opacity-40"
            >
              <Send size={14} /> {resending ? 'Se trimite...' : 'Trimite Parolă'}
            </button>
          )}
          <button onClick={() => setShowEditModal(true)} className="btn-secondary flex-1 sm:flex-none justify-center px-4 py-2.5 flex items-center gap-2 text-sm font-bold uppercase"><Edit size={14} /> Edit</button>
          {currentIsAdmin && <button onClick={() => setShowDeleteUserDialog(true)} className="p-2.5 text-red-500/50 hover:text-red-500 hover:bg-red-500/10 transition-colors rounded"><Trash2 size={18} /></button>}
        </div>
      </div>

      {/* Liste Membri Familie */}
      <div className="space-y-4">
        {persons.map((person) => {
          const active = subscriptions.find(s => s.person_id === person.id && s.status === 'active');
          const isStaff = person.isUser && (user.role === 'OWNER' || user.role === 'COACH');
          const needsSub = shouldShowSubUI(person);
          const personPlans = needsSub && !isStaff ? getPlansForPerson(person) : [];
          const grouped = groupByLocation(personPlans);
          const selectedPlanId = cart[person.id] || '';
          const selectedPlan = plans.find(p => p.id === selectedPlanId);
          const isChild = person.type === 'child';
          const previewPrice = selectedPlan ? (isChild ? selectedPlan.price * (1 - cartDiscount) : selectedPlan.price) : 0;

          const stripeClass = cart[person.id]
            ? 'bg-[#6db025]'
            : person.isUser
              ? 'bg-blue-400'
              : person.isSecondaryParent
                ? 'bg-cyan-400'
                : 'bg-purple-400';
          return (
            <div key={`${person.type}-${person.id}`} className={`relative bg-[#0F0F0F] border rounded transition-colors overflow-hidden ${cart[person.id] ? 'border-[#6db025]/40' : 'border-white/5 hover:border-white/10'}`}>
              <div className={`absolute left-0 top-0 bottom-0 w-[3px] ${stripeClass}`} />
              <div className="p-4 pl-5 sm:p-6 sm:pl-7">
                <div className="flex items-center justify-between gap-3 sm:gap-4 mb-4 sm:mb-5">
                  <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                    <div className={`w-10 h-10 sm:w-12 sm:h-12 flex-shrink-0 rounded-full flex items-center justify-center ${
                      person.isUser ? 'bg-blue-500/10 text-blue-400' :
                      person.isSecondaryParent ? 'bg-cyan-500/10 text-cyan-400' :
                      'bg-purple-500/10 text-purple-400'
                    }`}>
                      {isStaff ? <Shield size={20} /> : person.isUser ? <UserIcon size={20} /> : person.isSecondaryParent ? <UsersIcon size={20} /> : <Baby size={20} />}
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-heading text-lg sm:text-xl font-bold text-white uppercase break-words">{person.name}</h3>
                      <p className="text-[11px] text-white/30 uppercase font-black tracking-widest">
                        {person.isUser ? `Titular (${user.role})` : person.isSecondaryParent ? 'Al doilea Părinte' : 'Copil'}
                        {person.birth_date && (() => {
                          const age = Math.floor((new Date() - new Date(person.birth_date)) / (365.25 * 24 * 60 * 60 * 1000));
                          return <span className="ml-2 text-white/20">· {age} ani</span>;
                        })()}
                      </p>
                    </div>
                  </div>
                  {!person.isUser && !person.isSecondaryParent && (
                    <button onClick={() => setShowDeleteChildDialog(person.id)} className="p-2 text-red-500/50 hover:text-red-500 hover:bg-red-500/10 transition-colors rounded"><Trash2 size={18} /></button>
                  )}
                </div>

                {/* Abonament activ sau selector de plan */}
                {isStaff ? (
                  <div className="p-4 border border-white/5 bg-white/[0.01] text-white/20 text-[11px] font-bold uppercase tracking-widest flex items-center gap-2">
                    <Shield size={14} /> Cont de administrare (fără abonament)
                  </div>
                ) : needsSub ? (
                  <div className="space-y-3">
                    {/* Abonament activ */}
                    {active ? (
                      <div className="p-4 bg-[#6db025]/5 border border-[#6db025]/20">
                        <div className="flex justify-between mb-1">
                          <span className="text-[#6db025] text-[11px] font-black uppercase flex items-center gap-1.5"><Check size={12} /> Abonament Activ</span>
                          <span className="text-white/20 text-[11px] uppercase font-bold">{active.location}</span>
                        </div>
                        <p className="text-white font-bold text-base uppercase">{active.plan_name}</p>
                        <p className="text-white/40 text-sm mt-1">
                          {active.sessions_total
                            ? `${active.sessions_remaining} / ${active.sessions_total} ședințe rămase`
                            : active.expires_at
                              ? `Expiră: ${new Date(active.expires_at).toLocaleDateString('ro-RO')}`
                              : 'Activ'}
                        </p>
                      </div>
                    ) : (
                      <div className="p-3 bg-white/[0.02] border border-white/5 flex items-center gap-2">
                        <AlertTriangle size={13} className="text-white/20" />
                        <span className="text-white/20 text-[11px] font-black uppercase tracking-widest">Fără abonament activ</span>
                      </div>
                    )}

                    {/* Selector plan - only when no active subscription */}
                    {active ? (
                      <p className="flex items-center gap-2 text-yellow-400/80 text-[11px] uppercase font-bold tracking-widest">
                        <AlertTriangle size={13} /> Abonament deja activ - se poate adăuga unul nou după expirare
                      </p>
                    ) : personPlans.length > 0 ? (
                        <div className="space-y-2">
                          <label className="text-white/30 text-[11px] font-black uppercase tracking-widest">
                            Selectează program
                          </label>
                          <select
                            value={selectedPlanId}
                            onChange={(e) => {
                              const val = e.target.value;
                              setCart(prev => val ? { ...prev, [person.id]: val } : (() => { const n = {...prev}; delete n[person.id]; return n; })());
                            }}
                            className="w-full bg-[#050505] border border-white/10 p-3 text-white outline-none focus:border-[#6db025] text-base"
                          >
                            <option value="" className="bg-[#121212] text-white">- Alege programul -</option>
                            {Object.entries(grouped).map(([loc, locPlans]) => (
                              <optgroup key={loc} label={loc}>
                                {locPlans.map(p => (
                                  <option key={p.id} value={p.id} className="bg-[#121212] text-white">
                                    {p.activity} - {p.price} LEI{p.sessions ? ` · ${p.sessions} ședințe` : ''}
                                  </option>
                                ))}
                              </optgroup>
                            ))}
                          </select>

                          {selectedPlan && (
                            <div className="space-y-2">
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                <div className="space-y-1">
                                  <label className="text-white/30 text-[11px] font-black uppercase tracking-widest">
                                    Data de start <span className="text-white/20 normal-case font-normal">(implicit azi)</span>
                                  </label>
                                  <input
                                    type="date"
                                    value={customStartDates[person.id] || ''}
                                    onChange={(e) => setCustomStartDates(prev => ({
                                      ...prev,
                                      [person.id]: e.target.value || undefined
                                    }))}
                                    className="w-full bg-[#050505] border border-white/10 p-3 text-white outline-none focus:border-[#6db025] text-base"
                                  />
                                </div>
                                {selectedPlan.sessions ? (
                                  <div className="space-y-1">
                                    <label className="text-white/30 text-[11px] font-black uppercase tracking-widest">
                                      Ședințe folosite <span className="text-white/20 normal-case font-normal">(implicit 0)</span>
                                    </label>
                                    <input
                                      type="number"
                                      min="0"
                                      max={selectedPlan.sessions}
                                      inputMode="numeric"
                                      placeholder="0"
                                      value={customSessionsUsed[person.id] ?? ''}
                                      onChange={(e) => setCustomSessionsUsed(prev => ({
                                        ...prev,
                                        [person.id]: e.target.value
                                      }))}
                                      className="w-full bg-[#050505] border border-white/10 p-3 text-white outline-none focus:border-[#6db025] text-base placeholder:text-white/20"
                                    />
                                  </div>
                                ) : null}
                              </div>
                              <div className="space-y-1">
                                <label className="text-white/30 text-[11px] font-black uppercase tracking-widest">
                                  Data expirării <span className="text-white/20 normal-case font-normal">(implicit start + {selectedPlan.validity_days} zile)</span>
                                </label>
                                <input
                                  type="date"
                                  value={customExpiryDates[person.id] || ''}
                                  onChange={(e) => setCustomExpiryDates(prev => ({
                                    ...prev,
                                    [person.id]: e.target.value || undefined
                                  }))}
                                  className="w-full bg-[#050505] border border-white/10 p-3 text-white outline-none focus:border-[#6db025] text-base"
                                />
                              </div>
                              {(() => {
                                const startVal = customStartDates[person.id];
                                const usedVal = parseInt(customSessionsUsed[person.id], 10) || 0;
                                const startD = startVal ? new Date(startVal) : new Date();
                                const expD = customExpiryDates[person.id]
                                  ? new Date(customExpiryDates[person.id])
                                  : new Date(startD.getTime() + selectedPlan.validity_days * 24 * 60 * 60 * 1000);
                                return (
                                  <p className="text-white/30 text-[11px] uppercase font-bold tracking-widest">
                                    Rezultat:{' '}
                                    {selectedPlan.sessions
                                      ? <span className="text-white/60">{selectedPlan.sessions - usedVal}/{selectedPlan.sessions} ședințe rămase</span>
                                      : null}
                                    {selectedPlan.sessions ? ' · ' : ''}
                                    <span className="text-white/60">expiră {expD.toLocaleDateString('ro-RO')}</span>
                                  </p>
                                );
                              })()}
                              <div className="flex items-center justify-between p-3 bg-[#6db025]/5 border border-[#6db025]/20 rounded-sm">
                                <div className="flex items-center gap-2 text-[11px] text-white/40 uppercase font-black">
                                  {cartDiscount > 0 && isChild && (
                                    <span className="flex items-center gap-1 text-[#6db025]"><Tag size={11} /> Reducere 10% Frați</span>
                                  )}
                                </div>
                                <span className="text-[#6db025] font-black text-lg">{previewPrice.toFixed(0)} LEI</span>
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <p className="text-white/20 text-[11px] uppercase font-bold">Nu există programe eligibile</p>
                      )}
                  </div>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>

      {/* Coș de vânzare */}
      {cartItems.length > 0 && (
        <div className="sticky bottom-4 bg-[#121212] border border-[#6db025]/30 p-5 rounded-sm shadow-2xl shadow-black/50">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="space-y-1.5">
              <div className="flex items-center gap-2 text-[11px] text-white/40 uppercase font-black tracking-widest mb-2">
                <ShoppingCart size={13} className="text-[#6db025]" />
                <span>{cartItems.length} abonament{cartItems.length > 1 ? 'e' : ''} selectat{cartItems.length > 1 ? 'e' : ''}</span>
              </div>
              {cartItems.map(item => (
                <div key={item.personId} className="flex items-center justify-between gap-6 text-sm">
                  <span className="text-white/60 font-bold uppercase truncate max-w-[180px]">{item.person.name}</span>
                  <span className="text-white/40 truncate flex-1">{item.plan.activity}</span>
                  <div className="flex items-center gap-2">
                    {cartDiscount > 0 && item.person.type === 'child' && (
                      <span className="text-[10px] text-[#6db025] font-black uppercase">-10%</span>
                    )}
                    <span className="text-white font-bold whitespace-nowrap">{item.price.toFixed(0)} LEI</span>
                    <button onClick={() => setCart(prev => { const n = {...prev}; delete n[item.personId]; return n; })} className="text-white/20 hover:text-red-400 transition-colors ml-1"><X size={14} /></button>
                  </div>
                </div>
              ))}
              {cartItems.length > 1 && (
                <div className="flex justify-between pt-2 mt-2 border-t border-white/10 text-sm font-black uppercase">
                  <span className="text-white/40">Total</span>
                  <span className="text-[#6db025] text-base">{cartTotal.toFixed(0)} LEI</span>
                </div>
              )}
            </div>
            <button
              onClick={handleSellCart}
              disabled={saving}
              className="btn-primary px-8 py-4 font-black uppercase tracking-widest text-base whitespace-nowrap disabled:opacity-20 flex-shrink-0"
            >
              {saving ? 'Se procesează...' : cartItems.length > 1 ? `Finalizează ${cartItems.length} Abonamente` : 'Finalizează Vânzarea'}
            </button>
          </div>
        </div>
      )}

      {/* Edit Profil Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="bg-[#121212] border-white/10 p-5 sm:p-8 shadow-2xl w-[calc(100vw-1.5rem)] max-w-md max-h-[92dvh] overflow-y-auto rounded-xl">
          <DialogHeader><DialogTitle className="text-white uppercase font-heading text-xl">Modificare Profil</DialogTitle></DialogHeader>
          <form onSubmit={handleUpdateUser} className="space-y-4 mt-6">
            <div className="space-y-1">
              <label className="text-[11px] text-white/20 uppercase font-black pl-1">Nume Complet</label>
              <input type="text" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} required className="w-full bg-[#0A0A0A] border border-white/10 p-4 text-white focus:border-[#6db025] outline-none" />
            </div>
            <div className="space-y-1">
              <label className="text-[11px] text-white/20 uppercase font-black pl-1">Email (opțional)</label>
              <input type="text" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} placeholder="Fără email" className="w-full bg-[#0A0A0A] border border-white/10 p-4 text-white focus:border-[#6db025] outline-none placeholder:text-white/20" />
            </div>
            <div className="space-y-1">
              <label className="text-[11px] text-white/20 uppercase font-black pl-1">Telefon (opțional)</label>
              <input type="text" value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} placeholder="Fără telefon" className="w-full bg-[#0A0A0A] border border-white/10 p-4 text-white focus:border-[#6db025] outline-none placeholder:text-white/20" />
            </div>
            <div className="space-y-1">
              <label className="text-[11px] text-white/20 uppercase font-black pl-1">Rol Sistem</label>
              <select value={editForm.role} onChange={(e) => setEditForm({ ...editForm, role: e.target.value })} className="w-full bg-[#0A0A0A] border border-white/10 p-4 text-white outline-none">
                <option value="USER">Client (Părinte)</option>
                <option value="COACH">Antrenor</option>
                <option value="OWNER">Administrator</option>
              </select>
            </div>

            {(editForm.role === 'USER' || user?.role === 'USER') && (
              <div className="space-y-3 pt-3 border-t border-white/5">
                <label className="flex items-center gap-3 cursor-pointer p-3 bg-white/[0.02] border border-white/5 rounded-sm">
                  <input type="checkbox" checked={editForm.can_receive_subscriptions} onChange={(e) => setEditForm({...editForm, can_receive_subscriptions: e.target.checked})} className="w-4 h-4 accent-[#6db025]" />
                  <div>
                    <span className="text-white text-base font-bold block">Poate lua abonamente personale</span>
                    <span className="text-white/40 text-[11px] uppercase font-bold tracking-widest">Adultul participă activ la cursuri</span>
                  </div>
                </label>
                <div className="space-y-2 pt-1">
                  <p className="text-white/30 text-[11px] uppercase font-bold tracking-widest">Al doilea părinte (opțional)</p>
                  <input type="text" placeholder="Nume părinte 2" value={editForm.secondary_parent_name} onChange={(e) => setEditForm({...editForm, secondary_parent_name: e.target.value})} className="w-full bg-[#0A0A0A] border border-white/10 p-3 text-white focus:border-[#6db025] outline-none text-base placeholder:text-white/20" />
                  <input type="text" placeholder="Telefon părinte 2 (opțional)" value={editForm.secondary_parent_phone} onChange={(e) => setEditForm({...editForm, secondary_parent_phone: e.target.value})} className="w-full bg-[#0A0A0A] border border-white/10 p-3 text-white focus:border-[#6db025] outline-none text-base placeholder:text-white/20" />
                </div>
              </div>
            )}

            <button type="submit" disabled={saving} className="btn-primary w-full py-4 uppercase font-black tracking-widest mt-4">{saving ? 'Salvare...' : 'Actualizează'}</button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal Adăugare Copil */}
      <Dialog open={showAddChildModal} onOpenChange={setShowAddChildModal}>
        <DialogContent className="bg-[#121212] border-white/10 p-5 sm:p-8 shadow-2xl w-[calc(100vw-1.5rem)] max-w-md rounded-xl">
          <DialogHeader><DialogTitle className="text-white uppercase font-heading text-xl">Adăugare Copil</DialogTitle></DialogHeader>
          <form onSubmit={handleAddChild} className="space-y-4 mt-6">
            <input type="text" value={newChild.name} onChange={(e) => setNewChild({ ...newChild, name: e.target.value })} required className="w-full bg-[#0A0A0A] border border-white/10 p-4 text-white focus:border-[#6db025] outline-none placeholder:text-white/10" placeholder="Numele copilului" />
            <input type="date" value={newChild.birth_date} onChange={(e) => setNewChild({ ...newChild, birth_date: e.target.value })} className="w-full bg-[#0A0A0A] border border-white/10 p-4 text-white focus:border-[#6db025] outline-none" />
            <button type="submit" disabled={saving} className="btn-primary w-full py-4 uppercase font-black tracking-widest mt-4">Confirmă</button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialoguri Confirmare Ștergere */}
      <AlertDialog open={showDeleteUserDialog} onOpenChange={setShowDeleteUserDialog}>
        <AlertDialogContent className="bg-[#121212] border-white/10"><AlertDialogHeader><AlertDialogTitle className="text-white uppercase font-heading text-red-500">Eliminare Cont?</AlertDialogTitle><AlertDialogDescription className="text-white/40 text-base">Toate datele acestui utilizator vor fi șterse din sistem.</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel className="bg-white/5 border-0 text-white">Anulează</AlertDialogCancel><AlertDialogAction onClick={handleDeleteUser} className="bg-red-500 text-white">Șterge</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!showDeleteChildDialog} onOpenChange={() => setShowDeleteChildDialog(null)}>
        <AlertDialogContent className="bg-[#121212] border-white/10"><AlertDialogHeader><AlertDialogTitle className="text-white uppercase font-heading">Șterge Copilul</AlertDialogTitle><AlertDialogDescription className="text-white/40 text-base">Vei elimina înregistrarea acestui copil.</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel className="bg-white/5 border-0 text-white">Anulează</AlertDialogCancel><AlertDialogAction onClick={() => handleDeleteChild(showDeleteChildDialog)} className="bg-red-500 text-white">Șterge</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default UserDetailPage;
